import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FileCode2, Github, Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsApi } from "@/api/projects.ts";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Field, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { ImportWizard } from "./ImportWizard.tsx";

type Source = "blank" | "upload" | "github";

/** "Create a new project" modal, mirroring the original: name, starting point, then create. */
export function CreateProjectDialog({ onClose, initialSource = "blank" }: { onClose: () => void; initialSource?: Source }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [source, setSource] = useState<Source>(initialSource);
  const [created, setCreated] = useState<{ id: string } | null>(null);
  const m = useMutation({
    mutationFn: async () => {
      const p = await projectsApi.create({ name: form.name, description: form.description, limits: {}, ...(form.slug ? { slug: form.slug } : {}) });
      // Every new project starts its container right away.
      await projectsApi.start(p.id).catch(() => undefined);
      return p;
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
      if (source === "blank") {
        onClose();
        void nav({ to: "/projects/$projectId", params: { projectId: p.id } });
      } else setCreated({ id: p.id });
    },
  });
  const finish = () => {
    if (!created) return;
    onClose();
    void nav({ to: "/projects/$projectId", params: { projectId: created.id } });
  };
  const sources: { id: Source; icon: typeof FileCode2; title: string; desc: string; soon?: boolean }[] = [
    { id: "blank", icon: FileCode2, title: t("create.blank"), desc: t("create.blankDesc") },
    { id: "upload", icon: Upload, title: t("create.upload"), desc: t("create.uploadDesc") },
    { id: "github", icon: Github, title: t("create.github"), desc: t("create.githubDesc") },
  ];
  if (created) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
        <div className="w-full max-w-2xl space-y-4 rounded-lg border border-border bg-surface p-5 shadow-xl">
          <div>
            <h2 className="text-lg font-semibold">{source === "upload" ? t("create.upload") : t("create.github")} · {form.name}</h2>
            <p className="text-sm text-fg-muted">{t("create.importSubtitle")}</p>
          </div>
          <ImportWizard projectId={created.id} source={source === "github" ? "github" : "upload"} onDone={finish} onCancel={finish} />
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <form className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("create.title")}</h2>
            <p className="text-sm text-fg-muted">{t("create.subtitle")}</p>
          </div>
          <button type="button" aria-label={t("common.close")} onClick={onClose} className="rounded p-1 hover:bg-surface-2"><X className="size-5" /></button>
        </div>
        {m.error && <Alert tone="danger">{toMsg(m.error)}</Alert>}
        <Field label={t("projects.name")}>
          <Input required autoFocus maxLength={80} placeholder={t("create.namePlaceholder")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="space-y-2">
          <div className="text-sm font-medium">{t("create.source")}</div>
          <div className="grid gap-2">
            {sources.map((s) => (
              <button key={s.id} type="button" disabled={s.soon} onClick={() => setSource(s.id)} className={cn("flex items-start gap-3 rounded-md border p-3 text-start", source === s.id ? "border-accent bg-accent-soft/40" : "border-border hover:bg-surface-2", s.soon && "opacity-50")}>
                <s.icon className="mt-0.5 size-4 shrink-0" />
                <div>
                  <div className="text-sm font-medium">{s.title} {s.soon && <span className="ms-1 rounded bg-surface-2 px-1 text-[10px] text-fg-muted">{t("common.soon")}</span>}</div>
                  <div className="text-xs text-fg-muted">{s.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-fg-muted">{t("create.advanced")}</summary>
          <div className="mt-2 grid gap-3">
            <Field label={t("projects.slug")} hint={t("projects.slugHint")}>
              <Input dir="ltr" className="font-mono" pattern="[a-z0-9](?:[a-z0-9\-]{1,38}[a-z0-9])?" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} />
            </Field>
            <Field label={t("projects.description")}>
              <Input maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>
        </details>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={m.isPending}>{source === "blank" ? t("projects.create") : t("create.createAndImport")}</Button>
        </div>
      </form>
    </div>
  );
}
